const PZH_APP_ID = '96c3f77d4a8c4ee8ba7c962c240c363b';
// const PZH_APP_ID = 'c5f73ee9bf0142fab45f9561ec5964c7';//测试

/*
使用方式：

import {
  initTianrongTracking,
  trackTianrongEvent,
  isTianrongReady,
} from './tianrongTracking';

// 1. 初始化 SDK，并自动上报一次 init 和 load 访问量事件。
initTianrongTracking({
  configOptions: {
    sdkUrl: 'https://dcdn.thecover.cn/tianrong/tianfu_cio_pro.js',
    areaId: '租户 ID',
    accessToken: '授权 Token',
    appId: '应用 ID',
    appName: '报名',
    // false：只手动上报访问量和业务事件；
    // true：由 SDK 自动采集点击、请求、错误等事件，封装不重复手动上报 init/load。
    autoTrack: false,
    isRealTime: false,
  },
  // PDF 2.5.3 的 load 事件业务参数，可按页面动态返回。
  getPageViewEventParam() {
    return {
      title: document.title,
      sourceType: 1,
      sourceId: '业务对象 ID',
      channelName: '报名',
      loadTime: 0,
    };
  },
  onReady() {},
  onQueued() {},
  onTrackResponse() {},
  onError() {},
}).catch(error => {
  console.error('天融采集初始化失败', error);
});

// 2. 在真实按钮 click 处理函数中调用自定义事件。
function handleSignupClick() {
  return trackTianrongEvent({
    eventId: 'custom_event_001',
    eventType: 'click',
    eventParam: {
      buttonName: '报名按钮',
    },
  });
}

注意：onReady/onQueued/onError 是本封装提供的回调；
onTrackResponse 对应 PDF 中 cio.track 的 callback 参数。
trackTianrongEvent 会等待初始化完成后再把事件交给 SDK；
不要在页面初始化时直接上报 click，应该在真实点击事件中调用。
isTianrongReady() 只用于查看默认 init/load 事件是否已经入队。
*/

// 页面初始化和访问量事件：
// 0/init：SDK 初始化事件，2/load：页面加载事件，可作为访问量事件。
const TIANRONG_EVENTS = {
  INIT: {
    eventId: 0,
    eventType: 'init',
  },
  PAGE_VIEW: {
    eventId: 2,
    eventType: 'load',
  },
};

// 生产环境 SDK 地址。测试环境可改为：
// https://dcdn.thecover.cn/tianrong/tianfu_cio.js
const DEFAULT_CONFIG = {
  sdkUrl: 'https://dcdn.thecover.cn/tianrong/tianfu_cio_pro.js',
  // sdkUrl: 'https://dcdn.thecover.cn/tianrong/tianfu_cio.js',//测试环境
  areaId: '0400', // 天融采集平台分配的租户 ID
  accessToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6Imp3dCJ9.eyJhcHBLZXkiOiIwNDAwX1VNTWhEWTM5IiwiYXBwU2VjcmV0IjoiQ1lUNFp6cHVyakEzbENVY0VBbHVIODYyUXJDMEJ2eHYifQ.WM9N-tvCeMkroZVjccSXAi-xf4_CtPxeqdoTzRswZLQ', // 天融采集平台分配的授权 Token
  appId: PZH_APP_ID, // 应用 ID，当前为攀枝花站点
  appName: '攀枝花站点', // 应用名称
  autoTrack: false, // 是否开启全量自动采集；只统计访问量时保持 false
  isRealTime: true, // 是否实时上报，按手册参数传给 SDK
  pageViewEventParam: {}, // load 事件的业务参数
};

let sdkPromise;
let trackingPromise;
let tianrongReady = false;

function getSdkUrl(config) {
  // 参数名称按手册约定：
  // aid=应用 ID，appid=租户 ID，an=应用名称，at=自动采集，
  // ac=授权 Token，rt=实时上报。
  const query = [
    ['aid', config.appId],
    ['appid', config.areaId],
    ['an', config.appName || ''],
    ['at', config.autoTrack],
    ['ac', config.accessToken],
    ['rt', config.isRealTime],
    // 每次加载使用新的时间戳，避免浏览器或 CDN 命中旧版 SDK 缓存。
    ['t', Date.now()],
  ]
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');

  return `${config.sdkUrl}?${query}`;
}

function loadSdk(config) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('天融采集 SDK 只能在浏览器环境中使用'));
  }

  if (window.cio) return Promise.resolve(window.cio);
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const previousOnload = window.onload;
    const previousOnbeforeunload = window.onbeforeunload;
    const script = document.createElement('script');
    script.async = true;
    script.src = getSdkUrl(config);
    script.onload = () => {
      // SDK 会直接覆盖宿主项目的 onload/onbeforeunload，这里恢复原处理函数。
      window.onload = previousOnload;
      window.onbeforeunload = previousOnbeforeunload;

      if (window.cio) {
        resolve(window.cio);
      } else {
        reject(new Error('天融采集 SDK 加载完成，但未找到 window.cio'));
      }
    };
    script.onerror = () => {
      window.onload = previousOnload;
      window.onbeforeunload = previousOnbeforeunload;
      reject(new Error('天融采集 SDK 加载失败'));
    };
    (document.head || document.body).appendChild(script);
  }).catch(error => {
    // 加载失败后允许后续调用重新加载 SDK。
    sdkPromise = null;
    throw error;
  });

  return sdkPromise;
}

function callCallback(callback, ...args) {
  if (typeof callback === 'function') {
    callback(...args);
  }
}

function getPageViewEventParam(config) {
  const dynamicParam =
    typeof config.getPageViewEventParam === 'function'
      ? config.getPageViewEventParam()
      : {};

  return {
    // PDF 中 load 事件的标题，默认取当前页面标题。
    title: document.title || config.appName || '',
    // 页面来源地址不是 PDF load 示例的业务字段，但保留用于来源分析。
    refererUrl: document.referrer,
    ...config.pageViewEventParam,
    ...dynamicParam,
  };
}

/**
 * 初始化天融采集 SDK。
 * 以下回调由本封装提供，不是 PDF 中定义的 window.cio 原生方法：
 * onReady：SDK 脚本加载完成并完成 cio.init 后触发。
 * onQueued：访问量事件已交给 SDK 队列，不代表服务端已返回成功。
 * onTrackResponse：对应 PDF 中 cio.track 的 callback 参数。
 * onError：脚本加载或初始化异常时触发。
 *
 * autoTrack=false：
 *   封装手动上报 init/load，返回 Promise 在这两个事件进入 SDK 队列后完成。
 *
 * autoTrack=true：
 *   交给 SDK 自动采集点击、请求、错误等技术事件；
 *   由于 SDK 的自动 load 入口依赖 window.onload，封装会可靠地补发一次
 *   init/load，避免 SDK 晚初始化或页面已经完成加载时漏记访问量。
 *
 * 两种模式下，返回 Promise 都只代表事件已交给 SDK 处理，
 * 不代表服务端已经收到请求。
 */
export function initTianrongTracking(options = {}) {
  if (trackingPromise) return trackingPromise;

  tianrongReady = false;

  const {
    onReady,
    onQueued,
    onTrackResponse,
    onError,
    getPageViewEventParam,
    configOptions = {},
  } = options;
  // configOptions 用于覆盖 DEFAULT_CONFIG，后传入的配置优先。
  const config = {
    ...DEFAULT_CONFIG,
    ...configOptions,
    getPageViewEventParam:
      getPageViewEventParam || configOptions.getPageViewEventParam,
  };

  if (!config.areaId || !config.accessToken) {
    const error = new Error('天融采集缺少 areaId 或 accessToken');
    callCallback(onError, error);
    return Promise.reject(error);
  }

  trackingPromise = loadSdk(config).then(cio => {
    // 当前 SDK 的 cio.init 是同步方法；Promise.resolve 同时兼容未来异步实现。
    const initResult =
      typeof cio.init === 'function'
        ? cio.init({
            areaId: config.areaId,
            appId: config.appId,
            appName: config.appName,
            autoTrack: config.autoTrack,
          })
        : undefined;

    return Promise.resolve(initResult).then(() => {
      callCallback(onReady, cio);

      // 开启全量自动采集时，SDK 会自动监听点击、XHR、错误等事件。
      // SDK 自带的 window.onload 会被 loadSdk 恢复为宿主页面的处理函数，
      // 因此这里由封装统一补发一次 init/load，并标记 load_flag 避免 SDK
      // 的 load 监听再次重复上报。
      if (config.autoTrack) {
        return trackPageView(cio, config, onQueued, onTrackResponse);
      }

      return waitForPageLoad(() =>
        trackPageView(cio, config, onQueued, onTrackResponse)
      );
    });
  });

  trackingPromise.catch(() => {
    tianrongReady = false;
    trackingPromise = null;
  });

  trackingPromise.catch(error => {
    callCallback(onError, error);
  });

  return trackingPromise;
}

function waitForPageLoad(callback) {
  if (document.readyState === 'complete') {
    return Promise.resolve().then(callback);
  }

  return new Promise(resolve => {
    const onLoad = () => {
      window.removeEventListener('load', onLoad);
      resolve(callback());
    };
    window.addEventListener('load', onLoad);
  });
}

function trackPageView(cio, config, onQueued, onTrackResponse) {
  if (typeof cio.initLoad === 'function') {
    cio.initLoad();
  }

  if (typeof cio.track !== 'function') {
    throw new Error('天融采集 SDK 缺少 cio.track 方法');
  }

  // SDK 自动模式自己的 load 监听会检查这个标记。提前设置可避免重复上报。
  if (config.autoTrack && typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem('load_flag', '1');
  }

  const initEvent = { ...TIANRONG_EVENTS.INIT };
  const pageViewEvent = {
    ...TIANRONG_EVENTS.PAGE_VIEW,
    eventParam: getPageViewEventParam(config),
    callback: response => callCallback(onTrackResponse, response),
  };

  cio.track(initEvent);
  cio.track(pageViewEvent);
  callCallback(onQueued, {
    event: {
      eventId: pageViewEvent.eventId,
      eventType: pageViewEvent.eventType,
      eventParam: pageViewEvent.eventParam,
    },
    status: 'queued',
  });
  tianrongReady = true;
  return cio;
}

/**
 * 在 SDK 初始化和默认访问量事件入队后，上报其他自定义事件。
 * 返回 Promise 表示自定义事件已经交给 SDK 队列，不代表服务端已响应。
 */
export function trackTianrongEvent(event = {}) {
  if (!trackingPromise) {
    return Promise.reject(new Error('请先调用 initTianrongTracking'));
  }

  return trackingPromise.then(cio => {
    if (typeof cio.track !== 'function') {
      throw new Error('天融采集 SDK 缺少 cio.track 方法');
    }

    cio.track(event);
    return event;
  });
}

// 只表示默认 init/load 事件已经进入 SDK 队列，不代表服务端已经返回成功。
export function isTianrongReady() {
  return tianrongReady;
}

export {
  DEFAULT_CONFIG,
  PZH_APP_ID,
  TIANRONG_EVENTS,
  getSdkUrl,
};



// WEBPACK FOOTER //
// ./src/common/js/tianrongTracking.js